import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, Home, MapPin, Search, Sparkles, Ticket } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { destinationApi } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { asArray } from "@/lib/asArray";
import type { Destination } from "@/types";

const HERO_IMG =
  "https://images.unsplash.com/photo-1524492412937-b28074a5d7da?auto=format&fit=crop&w=2000&q=80";
const money = (n: number) => `₹${n.toLocaleString("en-IN")}`;

export default function Destinations() {
  const navigate = useNavigate();
  const [all, setAll] = useState<Destination[] | null>(null);
  const [featuredIds, setFeaturedIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const [query, setQuery] = useState("");
  const [city, setCity] = useState<string>("All");
  const [featuredOnly, setFeaturedOnly] = useState(false);

  // Fetch all destinations + the featured subset (so we can badge/filter them).
  useEffect(() => {
    destinationApi
      .list()
      .then((r) => setAll(asArray<Destination>(r.data)))
      .catch((e) => {
        setError(errorMessage(e, "Could not load destinations"));
        setAll([]);
      });
    destinationApi
      .list(true)
      .then((r) => setFeaturedIds(new Set(asArray<Destination>(r.data).map((d) => d.public_id))))
      .catch(() => {});
  }, []);

  const cities = useMemo(() => {
    const set = new Set((all ?? []).map((d) => d.city).filter(Boolean));
    return ["All", ...Array.from(set).sort()];
  }, [all]);

  const filtered = useMemo(() => {
    let list = all ?? [];
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((d) =>
        `${d.name} ${d.city} ${d.tagline ?? ""} ${d.description ?? ""}`.toLowerCase().includes(q),
      );
    }
    if (city !== "All") list = list.filter((d) => d.city === city);
    if (featuredOnly) list = list.filter((d) => featuredIds.has(d.public_id));
    return list;
  }, [all, query, city, featuredOnly, featuredIds]);

  const hasFeatured = featuredIds.size > 0;

  return (
    <div className="min-h-screen bg-cream">
      <Navbar overlay />

      {/* Hero */}
      <section className="relative w-full overflow-hidden">
        <img src={HERO_IMG} alt="Rajasthan heritage" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-900/90 via-navy-900/78 to-navy-900/95" />
        <div className="container-page relative pt-28 pb-10">
          <nav className="mb-3 flex items-center gap-1.5 text-sm text-white/70">
            <Link to="/" className="flex items-center gap-1 transition hover:text-gold-400">
              <Home size={14} /> Home
            </Link>
            <ChevronRight size={14} />
            <span className="text-white">Destinations</span>
          </nav>
          <h1 className="max-w-3xl font-display text-4xl font-bold text-white sm:text-5xl">
            Explore Heritage Destinations
          </h1>
          <p className="mt-2 max-w-xl text-white/75">
            Iconic forts, palaces and monuments across Rajasthan — book a Smart Heritage Pass in seconds.
          </p>

          {/* Search */}
          <div className="mt-6 flex max-w-2xl items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 backdrop-blur-xl">
            <Search size={18} className="text-white/60" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search a monument or city (e.g. Amber Fort, Jaipur)"
              className="w-full bg-transparent py-1.5 text-sm text-white placeholder:text-white/50 focus:outline-none"
            />
          </div>
        </div>
      </section>

      <main className="container-page py-8">
        {error && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
        )}

        {/* Filters */}
        <div className="mb-6 flex flex-wrap items-center gap-2">
          {cities.map((c) => {
            const on = city === c;
            return (
              <button
                key={c}
                onClick={() => setCity(c)}
                className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition ${
                  on
                    ? "border-iris-500 bg-iris-500 text-white"
                    : "border-slate-200 bg-white text-navy-800 hover:bg-slate-50"
                }`}
              >
                {c}
              </button>
            );
          })}
          {hasFeatured && (
            <button
              onClick={() => setFeaturedOnly((v) => !v)}
              className={`ml-auto flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-medium transition ${
                featuredOnly
                  ? "border-gold-500 bg-gold-500 text-navy-900"
                  : "border-slate-200 bg-white text-navy-800 hover:bg-slate-50"
              }`}
            >
              <Sparkles size={14} /> Featured only
            </button>
          )}
        </div>

        {/* Count */}
        {all !== null && (
          <p className="mb-4 text-sm text-slate-600">
            <span className="font-semibold text-navy-900">{filtered.length}</span> destination
            {filtered.length === 1 ? "" : "s"}
            {city !== "All" ? ` in ${city}` : ""}
          </p>
        )}

        {/* Grid */}
        {all === null ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-80 animate-pulse rounded-2xl bg-white" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white py-16 text-center">
            <p className="font-display text-lg font-semibold text-navy-900">No destinations found</p>
            <p className="mt-1 text-sm text-slate-500">Try a different city or clear your search.</p>
            <button
              onClick={() => {
                setQuery("");
                setCity("All");
                setFeaturedOnly(false);
              }}
              className="btn-outline mt-4"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((d) => (
              <DestinationCard
                key={d.public_id}
                d={d}
                featured={featuredIds.has(d.public_id)}
                onBook={() => navigate("/dashboard?tab=bookings")}
              />
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}

function DestinationCard({
  d,
  featured,
  onBook,
}: {
  d: Destination;
  featured: boolean;
  onBook: () => void;
}) {
  return (
    <article className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-card transition duration-300 hover:-translate-y-1 hover:shadow-lift">
      <div className="relative h-48 w-full overflow-hidden bg-slate-100">
        {d.image_url ? (
          <img
            src={d.image_url}
            alt={d.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="grid h-full place-items-center text-sm text-slate-300">No image</div>
        )}
        {featured && (
          <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-gold-500 px-2.5 py-1 text-[11px] font-semibold text-navy-900 shadow">
            <Sparkles size={11} /> Featured
          </span>
        )}
        <span className="absolute right-3 top-3 rounded-full bg-navy-900/80 px-2.5 py-1 text-[11px] font-medium text-white backdrop-blur">
          <MapPin size={11} className="mr-0.5 inline" /> {d.city}
        </span>
      </div>

      <div className="flex flex-1 flex-col p-4">
        <h3 className="font-display text-lg font-semibold text-navy-900">{d.name}</h3>
        {d.tagline && <p className="mt-0.5 text-sm font-medium text-iris-600">{d.tagline}</p>}
        {d.description && (
          <p className="mt-2 line-clamp-2 text-sm text-slate-500">{d.description}</p>
        )}

        <div className="mt-4 flex items-end justify-between gap-2 border-t border-slate-100 pt-4">
          <div>
            <div className="text-xs text-slate-500">Entry from</div>
            <div className="font-display text-xl font-bold text-navy-900">{money(d.ticket_price)}</div>
          </div>
          <button onClick={onBook} className="btn-primary px-4 py-2 text-sm">
            <Ticket size={15} /> Book Pass
          </button>
        </div>
      </div>
    </article>
  );
}